/**
 * Hand off free-pass PII to the thank-you page for Google Ads enhanced conversions.
 * @see https://support.google.com/google-ads/answer/13258081
 */

const EC_PAYLOAD_KEY = "gf_free_pass_ec_payload";
const TY_TAG_DONE_KEY = "gf_free_pass_ty_gtag_done";

/** Gmail / Googlemail normalization per Google Ads enhanced conversions guidance. */
export function normalizeEmailForEnhancedConversions(email) {
  if (!email || typeof email !== "string") return "";
  let normalized = email.trim().toLowerCase();
  const parts = normalized.split("@");
  if (parts.length === 2 && /^(gmail|googlemail)\.com$/.test(parts[1])) {
    parts[0] = parts[0].replace(/\./g, "");
    normalized = `${parts[0]}@${parts[1]}`;
  }
  return normalized.trim();
}

/** E.164 for US numbers; returns empty string if not usable. */
export function normalizePhoneE164Us(phone) {
  if (!phone || typeof phone !== "string") return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
}

/** Call on successful free-pass submit before navigating to thank-you. */
export function stageFreePassUserDataForAds({ email, phone }) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(TY_TAG_DONE_KEY);
    const payload = {
      email: typeof email === "string" ? email.trim() : "",
      phone: typeof phone === "string" ? phone.trim() : "",
    };
    if (!payload.email && !payload.phone) return;
    sessionStorage.setItem(EC_PAYLOAD_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Read and remove staged payload (one shot). */
function takeStagedFreePassUserDataForAds() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(EC_PAYLOAD_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(EC_PAYLOAD_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      email: typeof parsed.email === "string" ? parsed.email : "",
      phone: typeof parsed.phone === "string" ? parsed.phone : "",
    };
  } catch {
    return null;
  }
}

/**
 * Build gtag user_data object (plaintext — Google hashes). Omit empty fields.
 * @returns {Record<string, string>|null}
 */
export function buildGtagUserDataFromStagedFreePass() {
  const staged = takeStagedFreePassUserDataForAds();
  if (!staged) return null;
  const email = normalizeEmailForEnhancedConversions(staged.email);
  const phone = normalizePhoneE164Us(staged.phone);
  /** @type {Record<string, string>} */
  const userData = {};
  if (email) userData.email = email;
  if (phone) userData.phone_number = phone;
  return Object.keys(userData).length ? userData : null;
}

export function hasFreePassThankYouGtagFired() {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(TY_TAG_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markFreePassThankYouGtagFired() {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(TY_TAG_DONE_KEY, "1");
  } catch {
    /* ignore */
  }
}
